import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { X } from "lucide-react";
import { ChapterTag } from "./shared";

function ParallaxImg({ src, i, range = 80, className = "", onClick }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [range, -range]);
  return (
    <motion.figure
      ref={ref}
      data-testid={`gallery-item-${i}`}
      data-cursor="hover"
      onClick={onClick}
      initial={{ opacity: 0, scale: 1.08 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative m-0 cursor-none overflow-hidden rounded-xl ${className}`}
    >
      <motion.img
        style={{ y }}
        src={src}
        alt=""
        loading="lazy"
        className="h-[115%] w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-deep-black/0 transition-colors duration-500 group-hover:bg-deep-black/10" />
    </motion.figure>
  );
}

export default function Chapter5Gallery({ config }) {
  const imgs = config.gallery;
  const [active, setActive] = useState(null);

  return (
    <section className="relative overflow-hidden bg-warm-white py-28 md:py-40" data-testid="chapter-5">
      <div className="mx-auto max-w-7xl px-6 md:px-16">
        <ChapterTag n={4} title="Nuestra historia" />
        <h2 className="mb-16 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-deep-black md:text-6xl">
          Momentos de una vida <span className="italic text-gold-soft">que amamos</span>.
        </h2>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <ParallaxImg src={imgs[0]} i={0} onClick={() => setActive(imgs[0])} className="col-span-12 h-[60vh] md:col-span-7" />
          <ParallaxImg src={imgs[1]} i={1} range={120} onClick={() => setActive(imgs[1])} className="col-span-12 h-[60vh] md:col-span-5 md:mt-16" />
          <ParallaxImg src={imgs[2]} i={2} range={60} onClick={() => setActive(imgs[2])} className="col-span-6 h-[40vh] md:col-span-4" />
          <ParallaxImg src={imgs[3]} i={3} range={140} onClick={() => setActive(imgs[3])} className="col-span-6 h-[40vh] md:col-span-4 md:-mt-10" />
          <ParallaxImg src={imgs[4] || imgs[0]} i={4} range={90} onClick={() => setActive(imgs[4] || imgs[0])} className="col-span-12 h-[46vh] md:col-span-4" />
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-deep-black/90 p-6 backdrop-blur-md"
          >
            <button
              data-testid="lightbox-close"
              className="absolute right-6 top-6 text-ivory/70 hover:text-gold"
              onClick={() => setActive(null)}
            >
              <X className="h-7 w-7" strokeWidth={1} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              src={active}
              alt=""
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
